import React from 'react'
import { useNavigate } from 'react-router-dom'

import MonthlyActivityChart from './MonthlyActivityChart'
import MonthlyChartHeader from './MonthlyChartHeader'
import MonthlyActivitiesFooter from './MonthlyActivitiesFooter'
import { TimelineSkeleton } from '../../../components_new/TimelineSkeleton'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import moment from 'moment'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { useActivity } from '@/modules/drivers/stores/activityStore'

const HEIGHT = 94

const WEEKLY_CHART_HEIGHT = 166
const PER_WEEK_CHART_HEIGHT = 37 + WEEKLY_CHART_HEIGHT + 12
const NAV_RESERVE = 56
const FOOTER_HEIGHT = 40
const FIT_SLACK = 8

const BUFFER_WEEKS = 20
const PREFETCH_THRESHOLD_DAYS = 42

type BufferRange = {
  start: moment.Moment
  end: moment.Moment
}

const MonthlyWrapper = ({
  ribbonRef,
  currentDate,
  weekOffset,
  companyId,
  driverId,
  onWheel,
}: {
  ribbonRef: React.RefObject<HTMLDivElement>
  currentDate: string
  weekOffset: number
  companyId: string
  driverId: number
  onWheel?: (e: React.WheelEvent) => void
}) => {
  const navigate = useNavigate()
  const { getDriverActivity } = useDriverActivityService()
  const { formatApiDate } = useDate()
  const { t } = useLocalize()
  const { refreshTrigger } = useActivity()
  const [weekRecompenses, setWeekRecompenses] = React.useState<any[]>([])
  const [weeklyRests, setWeeklyRests] = React.useState<any[]>([])
  const [places, setPlaces] = React.useState<any[]>([])
  const [weeklyReducedRests, setWeeklyReducedRests] = React.useState<any[]>([])
  const [chartWidth, setChartWidth] = React.useState(0)
  const [weeksToShow, setWeeksToShow] = React.useState(6)
  const [footerPos, setFooterPos] = React.useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })
  const [isInitialLoading, setIsInitialLoading] = React.useState(true)
  const [activities, setActivities] = React.useState<any[]>([])

  const rootRef = React.useRef<HTMLDivElement>(null)
  const bufferRangeRef = React.useRef<BufferRange | null>(null)
  const isFetchingMoreRef = React.useRef(false)
  const lastFetchKeyRef = React.useRef<string>('')

  const determineWidth = () => {
    const ribbon = ribbonRef.current
    if (!ribbon) return

    const rect = ribbon.getBoundingClientRect()
    setChartWidth(ribbon.clientWidth)
    setFooterPos({ left: rect.left, width: ribbon.clientWidth })

    const root = rootRef.current
    const navOffset = root
      ? root.getBoundingClientRect().top - rect.top
      : NAV_RESERVE
    const available =
      ribbon.clientHeight - navOffset - FOOTER_HEIGHT - FIT_SLACK
    const fit = Math.max(1, Math.floor(available / PER_WEEK_CHART_HEIGHT))
    setWeeksToShow((prev) => (prev !== fit ? fit : prev))
  }

  const getFirstDayOfWeek = (date: Date): moment.Moment => {
    const momentDate = moment(date)
    const firstDayDate =
      momentDate.isoWeekday() !== 7
        ? momentDate.startOf('week').weekday(1)
        : momentDate.clone().add(-1, 'days').startOf('week').weekday(1)
    return firstDayDate
  }

  const getCurrentDate = () => {
    const [year, month] = currentDate.split('-').map(Number)
    const baseDate = new Date(year, month - 1)
    return moment(baseDate).add(weekOffset, 'weeks')
  }

  const startDate = getFirstDayOfWeek(getCurrentDate().toDate())

  const visibleStart = startDate.clone()
  const visibleEnd = startDate.clone().add(42, 'days')

  const isVisibleCovered = (): boolean => {
    const buf = bufferRangeRef.current
    if (!buf) return false
    return (
      visibleStart.isSameOrAfter(buf.start) &&
      visibleEnd.isSameOrBefore(buf.end)
    )
  }

  const fetchActivities = async (
    fetchStart: moment.Moment,
    fetchEnd: moment.Moment,
    opts: { replace?: boolean; showLoading?: boolean } = {},
  ) => {
    const { replace = false, showLoading = false } = opts

    if (showLoading) setIsInitialLoading(true)

    try {
      const result = await getDriverActivity({
        companyId,
        driverId,
        startTime: formatApiDate(fetchStart.toDate()),
        endTime: formatApiDate(fetchEnd.toDate()),
        includeTrips: false,
      })

      const newActivities = result.activities ? [...result.activities] : []
      const places = result.places ? [...result.places] : []
      const weeklyRests = result.weeklyRests ? [...result.weeklyRests] : []
      const weekRecompenses = result.weekRecompenses
        ? [...result.weekRecompenses]
        : []
      const weeklyReducedRests = result.weeklyReducedRests
        ? [...result.weeklyReducedRests]
        : []
      if (replace) {
        setActivities(newActivities)
        setPlaces(places)
        bufferRangeRef.current = {
          start: fetchStart.clone(),
          end: fetchEnd.clone(),
        }
        setWeekRecompenses(weekRecompenses)
        setWeeklyRests(weeklyRests)
        setWeeklyReducedRests(weeklyReducedRests)
      } else {
        setActivities((prev) => {
          const existingKeys = new Set(
            prev.map(
              (a: any) =>
                `${a.startTime}_${a.endTime}_${a.type || ''}_${a.entryType || ''}`,
            ),
          )
          const merged = [
            ...prev,
            ...newActivities.filter((a: any) => {
              const key = `${a.startTime}_${a.endTime}_${a.type || ''}_${a.entryType || ''}`
              return !existingKeys.has(key)
            }),
          ]
          return merged
        })

        const buf = bufferRangeRef.current
        if (buf) {
          bufferRangeRef.current = {
            start: moment.min(buf.start, fetchStart),
            end: moment.max(buf.end, fetchEnd),
          }
        } else {
          bufferRangeRef.current = {
            start: fetchStart.clone(),
            end: fetchEnd.clone(),
          }
        }
      }
    } catch (error) {
      console.error('Error loading monthly activities:', error)
    } finally {
      if (showLoading) setIsInitialLoading(false)
    }
  }

  const fetchFullBuffer = async (
    showLoading: boolean,
    force: boolean = false,
  ) => {
    const bufferStart = visibleStart.clone().add(-BUFFER_WEEKS, 'weeks')
    const bufferEnd = visibleEnd.clone().add(BUFFER_WEEKS, 'weeks')

    const fetchKey = `${bufferStart.format('YYYY-MM-DD')}_${bufferEnd.format('YYYY-MM-DD')}`
    if (fetchKey === lastFetchKeyRef.current && !force) return
    lastFetchKeyRef.current = fetchKey

    await fetchActivities(bufferStart, bufferEnd, {
      replace: true,
      showLoading,
    })
  }

  const checkAndPrefetch = async () => {
    const buf = bufferRangeRef.current
    if (!buf || isFetchingMoreRef.current) return

    const daysToBufferStart = visibleStart.diff(buf.start, 'days')
    const daysToBufferEnd = buf.end.diff(visibleEnd, 'days')

    if (daysToBufferStart < PREFETCH_THRESHOLD_DAYS) {
      isFetchingMoreRef.current = true
      const newStart = buf.start.clone().add(-BUFFER_WEEKS, 'weeks')
      await fetchActivities(newStart, buf.start.clone(), { replace: false })
      isFetchingMoreRef.current = false
    }

    if (daysToBufferEnd < PREFETCH_THRESHOLD_DAYS) {
      isFetchingMoreRef.current = true
      const newEnd = buf.end.clone().add(BUFFER_WEEKS, 'weeks')
      await fetchActivities(buf.end.clone(), newEnd, { replace: false })
      isFetchingMoreRef.current = false
    }
  }

  React.useEffect(() => {
    determineWidth()
    lastFetchKeyRef.current = ''
    fetchFullBuffer(true)
  }, [companyId, driverId, currentDate])

  const isInitialMountRef = React.useRef(true)
  React.useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    if (isVisibleCovered()) {
      checkAndPrefetch()
    } else {
      fetchFullBuffer(true)
    }
  }, [weekOffset])

  React.useEffect(() => {
    if (refreshTrigger > 0) {
      fetchFullBuffer(true, true)
    }
  }, [refreshTrigger])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      determineWidth()
    }, 20)
    return () => clearTimeout(timer)
  }, [activities])

  React.useEffect(() => {
    const onResize = () => determineWidth()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const renderCharts = () => {
    const charts = []
    let currentStartTime = visibleStart.clone()

    for (let i = 0; i < weeksToShow; i++) {
      charts.push(
        <div
          className="tw-overflow-x-hidden tw-overflow-y-auto"
          key={`${currentStartTime.format('YYYY-MM-DD')}`}
        >
          <div className="tw-bg-white tw-overflow-hidden">
            {chartWidth > 0 && (
              <div>
                <MonthlyChartHeader
                  startTime={currentStartTime}
                  days={7}
                  activities={activities}
                  translate={t}
                />
                <MonthlyActivityChart
                  width={chartWidth}
                  currentDate={currentDate}
                  height={HEIGHT}
                  key={i}
                  days={7}
                  startTime={currentStartTime}
                  activities={activities}
                  cardInserts={[]}
                  places={places}
                  dayActivities={[]}
                  violations={[]}
                  dailyRests={[]}
                  dailyPartialRegularRests={[]}
                  weeklyRests={weeklyRests}
                  weeklyReducedRests={weeklyReducedRests}
                  breaks={[]}
                  partialBreaks={[]}
                  weekRecompenses={weekRecompenses}
                  outOfScopes={[]}
                  onSelected={(date: moment.Moment) => {}}
                  onDaySelected={(date) => {
                    const formattedDate = moment(date).format('YYYY-MM-DD')
                    navigate(
                      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/dailyActivity/${formattedDate}`,
                    )
                  }}
                />
              </div>
            )}
          </div>
        </div>,
      )

      currentStartTime = currentStartTime.clone().add(7, 'days')
    }

    return charts
  }

  const renderSkeletons = () => {
    const rows = []
    let s = visibleStart.clone()

    for (let i = 0; i < weeksToShow; i++) {
      rows.push(
        <div
          className="tw-overflow-hidden tw-bg-white"
          style={{ marginBottom: 12 }}
          key={`sk-${s.format('YYYY-MM-DD')}`}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
            <div className="tw-h-3.5 tw-w-40 tw-animate-pulse tw-rounded tw-bg-gray-200" />
            <div className="tw-flex tw-gap-4">
              {[0, 1, 2, 3].map((k) => (
                <div
                  key={k}
                  className="tw-h-3.5 tw-w-12 tw-animate-pulse tw-rounded tw-bg-gray-200"
                />
              ))}
            </div>
          </div>
          <TimelineSkeleton
            width={chartWidth}
            height={WEEKLY_CHART_HEIGHT}
            startTime={s.clone()}
            isMonth={false}
          />
        </div>,
      )

      s = s.clone().add(7, 'days')
    }

    return rows
  }

  const displayContent = activities != null && activities.length > 0

  return (
    <div ref={rootRef} onWheel={onWheel} className="">
      {isInitialLoading && (
        <div
          style={{ position: 'relative' }}
          className="tw-overflow-x-hidden tw-overflow-y-auto"
        >
          {renderSkeletons()}
        </div>
      )}
      {displayContent && !isInitialLoading && chartWidth !== 0 && (
        <div>
          <div
            style={{ position: 'relative' }}
            className="tw-overflow-x-hidden tw-overflow-y-auto"
          >
            {renderCharts()}
          </div>
        </div>
      )}
      {!displayContent && !isInitialLoading && (
        <div
          className="no-data-wrapper"
        >
          {t('dailyActivities.no-data')}
        </div>
      )}

      {displayContent && !isInitialLoading && footerPos.width > 0 && (
        <MonthlyActivitiesFooter
          startTime={visibleStart}
          days={weeksToShow * 7}
          activities={activities}
          translate={t}
          left={footerPos.left}
          width={footerPos.width}
          height={FOOTER_HEIGHT}
        />
      )}
    </div>
  )
}

export default MonthlyWrapper
